import { useMemo, useState } from 'react';
import type { Package, SellPackage } from '@salonomy/types';
import { Button, Field, Modal, Pill, SegmentedControl, Select, TextArea } from '@salonomy/ui';
import { usePackages, useSellPackage } from '../../data/packages.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import './till.css';

/** What the till sells/issues a package as. SALE = money in; COMP = gifted balance, no money in. */
type Tender = SellPackage['tender'];

/**
 * WS-13 Feature 3a — SELL a prepaid package at the till, beside IssueGiftCardModal. The catalogue can
 * define recurring memberships (`membership_recurring`) and single-purpose packages (`purpose='single'`),
 * but BOTH are DEFERRED server-side — the sell route 422s either. So this form ONLY offers the SELLABLE
 * prepaid kinds (`prepaid_sessions` / `prepaid_value`, multi-purpose) — there's nothing here that would
 * 422 on kind/purpose.
 *
 * ACCOUNTING (the point — ADR-0009): a sale is a LIABILITY, not revenue. The till takes the money and the
 * API credits package_liability; revenue/VAT land only at redemption on the real serviced lines. So the
 * sale appears once and can never be counted twice. A COMP posts to comp_expense (the salon's own spend)
 * and requires a reason.
 *
 * HONESTY: the price shown is the catalogue list price (a server figure); nothing is recomputed here. Sell
 * is idempotent on a per-intent key minted once on submit; the button is disabled while pending so a
 * double-tap can't sell two instruments, and a 422/409 surfaces faithfully.
 */
export function SellPackageModal({
  clientId,
  clientName,
  onSold,
  onClose,
}: {
  /** The basket's client. A package is sold to a specific client (the API requires a clientId). */
  clientId: string | null;
  clientName?: string | null;
  /** Called with the sold package's price (pence) + tender so the till can note the sale. */
  onSold: (pricePence: number, tender: Tender) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const sell = useSellPackage();
  // Only active catalogue packages are offered for sale.
  const { data: page, isLoading } = usePackages(true, 0);

  // SELLABLE = prepaid kinds + multi-purpose only. Recurring memberships and single-purpose packages are
  // deferred server-side (422), so the picker never offers them — the form can't compose a sell that 422s.
  const sellable = useMemo<Package[]>(
    () =>
      (page?.data ?? []).filter((p) => p.kind !== 'membership_recurring' && p.purpose !== 'single'),
    [page],
  );

  const [packageId, setPackageId] = useState('');
  const [tender, setTender] = useState<Tender>('card');
  const [reason, setReason] = useState('');

  const selected = sellable.find((p) => p.id === packageId) ?? null;
  const isComp = tender === 'comp';
  const reasonRequired = isComp; // a comp is a gifted balance — it carries an audit reason (ADR-0009)
  const noClient = clientId == null;
  const canSell = !noClient && selected != null && (!reasonRequired || reason.trim().length > 0);

  async function submit() {
    if (!canSell || !selected || !clientId) return;
    try {
      // Mint the idempotency key once per intent; the button is disabled while pending so a double-tap
      // can't sell a second instrument, and the API is idempotent on this key.
      const cp = await sell.mutateAsync({
        packageId: selected.id,
        clientId,
        tender,
        reason: reason.trim() ? reason.trim() : undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      onSold(selected.pricePence, tender);
      toast.success(
        isComp
          ? `Comped ${cp.name} · ${money(selected.pricePence)}`
          : `Sold ${cp.name} · ${money(selected.pricePence)}`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not sell the package');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Sell package"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {!noClient ? (
            <Button onClick={submit} disabled={!canSell || sell.isPending}>
              {sell.isPending ? 'Selling…' : isComp ? 'Comp package' : 'Sell package'}
            </Button>
          ) : null}
        </>
      }
    >
      {noClient ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          A package is sold to a specific client, but this checkout has no client on it. Add a
          client to the booking first, then sell the package.
        </div>
      ) : isLoading ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Loading the catalogue…
        </div>
      ) : sellable.length === 0 ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          There are no sellable prepaid packages yet. A manager can create a prepaid-sessions or
          prepaid-value package on the Packages page. (Recurring memberships and single-purpose
          packages aren&rsquo;t sellable yet.)
        </div>
      ) : (
        <>
          {clientName ? (
            <div style={{ marginBottom: 8 }}>
              <Pill tone="neutral">For {clientName}</Pill>
            </div>
          ) : null}

          <Field label="Package">
            <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">Choose a package…</option>
              {sellable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {money(p.pricePence)}
                </option>
              ))}
            </Select>
          </Field>

          {selected ? (
            <div className="till-voucher__found" style={{ marginTop: 4 }}>
              <div className="till__line till__line--total">
                <span>Price</span>
                <span className="mono">{money(selected.pricePence)}</span>
              </div>
              <div className="till__line muted">
                <span>Validity</span>
                <span className="mono">
                  {selected.validityDays != null ? `${selected.validityDays} days` : 'No expiry'}
                </span>
              </div>
            </div>
          ) : null}

          <Field label="Paid by">
            <SegmentedControl<Tender>
              options={[
                { value: 'card', label: 'Card' },
                { value: 'cash', label: 'Cash' },
                { value: 'comp', label: 'Comp' },
              ]}
              value={tender}
              onChange={setTender}
            />
          </Field>

          {reasonRequired ? (
            <Field
              label="Comp reason"
              hint="A comped package is recorded against you with a reason."
            >
              <TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why this package is being comped"
                rows={2}
              />
            </Field>
          ) : null}

          <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
            {isComp
              ? 'A comped package is the salon’s own spend — it’s recorded as a liability the salon will honour, with no revenue or VAT at issue.'
              : 'A package sale is held as a liability, not revenue — the takings recognise it only as sessions are redeemed, so it’s never counted twice. No VAT at sale.'}
          </div>
        </>
      )}
    </Modal>
  );
}
